import updateCart from '~/firebase/update/updateCart';

import ordersAtom from '~/recoil/atom/orders';
import memberInfoAtom from '~/recoil/atom/memberInfo';
import isHostSelector from '~/recoil/selectors/isHost';
import cartStatusAtom from '~/recoil/atom/cartStatus';
import { useRecoilState, useRecoilValue } from 'recoil';

import { PLATFORM_ID } from '~/globalVariables';

import useCartId from '~/hooks/useCartId';

import * as fp from 'lodash/fp';

import { CartItem, CartType, StoreOrderInCart, CartStatus } from '~/types/cart';
import enterpriseGroupBuyOrdersAtom from '~/recoil/atom/enterpriseGroupBuy/enterpriseGroupBuyOrders';
import { useParams } from 'react-router-dom';

export default function useCartOrders() {
  const { cartId, cartType } = useCartId();
  const { enterpriseGroupBuyId = '' } = useParams();
  const [orders, setOrders] = useRecoilState(
    !!enterpriseGroupBuyId ? enterpriseGroupBuyOrdersAtom : ordersAtom,
  );
  const memberInfo = useRecoilValue(memberInfoAtom);
  const cartStatus = useRecoilValue(cartStatusAtom);
  const isHost = useRecoilValue(isHostSelector);

  const handleAddItem = async (storeId: string, storeName: string, item: CartItem) => {
    if (!cartId || !fp.eq(cartStatus, CartStatus.ACTIVE)) return;

    const updateOrders = fp.cond([
      [
        fp.eq(CartType.CART_PERSONAL),
        () => {
          const isStoreExist = orders.some((order) => fp.eq(order.storeId, storeId));
          return isStoreExist
            ? orders.map((order) =>
                fp.eq(order.storeId, storeId)
                  ? { ...order, items: [...order.items, { ...item }] }
                  : order,
              )
            : [
                ...orders,
                {
                  storeId: storeId,
                  name: storeName,
                  items: [item],
                  groupItems: [],
                },
              ];
        },
      ],
      [
        fp.eq(CartType.CART_GROUP),
        () => {
          const storeOrder = orders.find(fp.propEq('storeId', storeId));
          const groupItem = (storeOrder?.groupItems || []).find(
            fp.propEq('memberId', memberInfo.id),
          );

          const newGroupItem = {
            memberId: groupItem?.memberId || memberInfo.id,
            memberName: groupItem?.memberName || memberInfo.name,
            items: (groupItem?.items || []).concat([item]),
          };

          const newStoreOrder: StoreOrderInCart = {
            storeId: storeOrder?.storeId || storeId,
            name: storeOrder?.name || storeName,
            items: [],
            groupItems: groupItem // group item 原本不存在就新增，否則就取代
              ? (storeOrder?.groupItems || []).map((groupItem) =>
                  fp.eq(groupItem.memberId, memberInfo.id) ? newGroupItem : groupItem,
                )
              : (storeOrder?.groupItems || []).concat([newGroupItem]),
          };

          // Store Cart 原本不存在就新增，否則就取代
          return storeOrder
            ? orders.map((order) => (fp.eq(order.storeId, storeId) ? newStoreOrder : order))
            : orders.concat([newStoreOrder]);
        },
      ],
    ])(cartType);

    await updateCart(PLATFORM_ID, cartId, {
      orders: updateOrders,
    });
    if (!!enterpriseGroupBuyId) setOrders(updateOrders);
  };

  const handleDeleteItem = async (storeId: string, keyId: string) => {
    if (!cartId || !fp.eq(cartStatus, CartStatus.ACTIVE)) return;

    const updateOrders = fp.cond([
      [
        fp.eq(CartType.CART_PERSONAL),
        () => {
          return orders
            .map((order) =>
              fp.eq(storeId, order.storeId)
                ? fp.set(
                    'items',
                    order.items.filter((item) => !fp.eq(item.keyId, keyId)),
                  )(order)
                : order,
            )
            .filter(({ items }) => fp.gt(items.length, 0));
        },
      ],
      [
        fp.eq(CartType.CART_GROUP),
        () => {
          return orders
            .map((order) => {
              if (fp.eq(storeId, order.storeId)) {
                const newGroupItems = order.groupItems
                  .map((groupItem) =>
                    isHost || fp.eq(groupItem.memberId, memberInfo.id)
                      ? fp.set(
                          'items',
                          groupItem.items.filter((item) => !fp.eq(item.keyId, keyId)),
                        )(groupItem)
                      : groupItem,
                  )
                  .filter(({ items }) => fp.gt(items.length, 0));

                return fp.set('groupItems', newGroupItems)(order);
              }

              return order;
            })
            .filter(({ groupItems }) => fp.gt(groupItems.length, 0));
        },
      ],
    ])(cartType);

    await updateCart(PLATFORM_ID, cartId, {
      orders: updateOrders,
    });
    if (!!enterpriseGroupBuyId) setOrders(updateOrders);
  };

  const handleEditItem = async (storeId: string, updateItem: CartItem) => {
    if (!cartId || !fp.eq(cartStatus, CartStatus.ACTIVE)) return;

    const updateOrders = fp.cond([
      [
        fp.eq(CartType.CART_PERSONAL),
        () => {
          return orders.map((order) =>
            fp.eq(order.storeId, storeId)
              ? fp.set(
                  'items',
                  order.items.map((item) =>
                    fp.eq(item.keyId, updateItem.keyId) ? updateItem : item,
                  ),
                )(order)
              : order,
          );
        },
      ],
      [
        fp.eq(CartType.CART_GROUP),
        () => {
          return orders.map((order) => {
            if (fp.eq(order.storeId, storeId)) {
              const newGroupItems = order.groupItems.map((groupItem) =>
                isHost || fp.eq(groupItem.memberId, memberInfo.id)
                  ? fp.set(
                      'items',
                      groupItem.items.map((item) =>
                        fp.eq(item.keyId, updateItem.keyId) ? updateItem : item,
                      ),
                    )(groupItem)
                  : groupItem,
              );
              return fp.set('groupItems', newGroupItems)(order);
            }
            return order;
          });
        },
      ],
    ])(cartType);

    await updateCart(PLATFORM_ID, cartId, {
      orders: updateOrders,
    });
    if (!!enterpriseGroupBuyId) setOrders(updateOrders);
  };

  const handleClearOrders = async () => {
    if (!cartId || !fp.eq(cartStatus, CartStatus.ACTIVE)) return;

    await updateCart(PLATFORM_ID, cartId, {
      orders: [],
    });
    if (!!enterpriseGroupBuyId) setOrders([]);
  };

  return {
    handleAddItem,
    handleDeleteItem,
    handleEditItem,
    handleClearOrders,
  };
}
